'use client';

import { Checkbox, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import dayjs from 'dayjs';
import React from 'react';

import {
	convertToDate,
	formatDateForDatePicker,
	formatDateParkerStatusForDisplay,
	isOwnerPortal,
} from '../../../utils';

type ParkerStatusDetailColumnsProps = {
	editActionMode: boolean;
	editingRowIds: GridRowId[];
	setHasActionChange: (hasActionChange: boolean) => void;
	handleActionInfoUpdate: (id: string, field: string, value: any) => void;
	currentDate: Date;
	firstDays: Date[];
	lastDays: Date[];
	yesterday: Date;
	handleKeyDown: (e: React.KeyboardEvent) => void;
	renderEditAction: (params: any) => React.JSX.Element;
	renderDeleteAction: (params: any) => React.JSX.Element;
	updateActionMode: boolean;
	isWaitlistParker: boolean;
};

export const parkerStatusDetailColumns = ({
	editingRowIds,
	setHasActionChange,
	handleActionInfoUpdate,
	currentDate,
	firstDays,
	lastDays,
	yesterday,
	handleKeyDown,
	renderEditAction,
	renderDeleteAction,
	updateActionMode,
	isWaitlistParker,
}: ParkerStatusDetailColumnsProps): GridColDef[] => [
	{
		field: 'dateRecorded',
		headerName: 'Date Recorded',
		headerAlign: 'center' as const,
		cellClassName: 'tableCell',
		flex: 1.5,
		renderCell: (params) => params.row.dateRecorded || '',
		resizable: false,
		sortable: false,
	},
	{
		field: 'action',
		headerName: 'Action',
		headerAlign: 'center' as const,
		cellClassName: 'tableCell',
		flex: 1.5,
		renderCell: (params) =>
			editingRowIds.includes(params.id) ? (
				<Select
					required
					value={params.row.action || ''}
					onChange={(e) => {
						setHasActionChange(true);
						params.row.action = e.target.value;
						handleActionInfoUpdate(params.row.id, 'action', e.target.value);
					}}
					style={{ width: '100%' }}
				>
					<MenuItem value='Suspend'>Suspend</MenuItem>
					<MenuItem value='Terminate'>Terminate</MenuItem>
				</Select>
			) : (
				params.row.action || ''
			),
		resizable: false,
		sortable: false,
	},
	{
		field: 'startDate',
		headerName: 'Start Date',
		headerAlign: 'center' as const,
		cellClassName: 'tableCell',
		flex: 1,
		renderCell: (params) =>
			params.row.suspensionStartDate || params.row.terminationDate ? '' : params.row.startDate,
		resizable: false,
		sortable: false,
	},
	{
		field: 'suspensionStartDate',
		headerName: 'Suspension Start Date',
		headerAlign: 'center' as const,
		cellClassName: 'tableCell',
		flex: 1.5,
		renderCell: (params) => {
			if (params.row.action !== 'Terminate') {
				const isNewRow = params.row.isNew;
				const isPastDate = dayjs(params.row.suspensionStartDate).isSameOrBefore(currentDate);
				if (editingRowIds.includes(params.id)) {
					return (
						<Select
							value={
								updateActionMode
									? formatDateParkerStatusForDisplay(
											formatDateForDatePicker(params.row.suspensionStartDate),
										)
									: params.row.suspensionStartDate || ''
							}
							onChange={(e) => {
								setHasActionChange(true);
								params.row.suspensionStartDate = e.target.value;
								handleActionInfoUpdate(params.row.id, 'suspensionStartDate', e.target.value);
							}}
							style={{ width: '100%' }}
							readOnly={!isNewRow && isPastDate}
						>
							<MenuItem value={formatDateParkerStatusForDisplay(currentDate)}>Suspend Now</MenuItem>
							{firstDays?.map((option, index) => (
								<MenuItem key={index} value={formatDateParkerStatusForDisplay(option)}>
									{formatDateParkerStatusForDisplay(option)}
								</MenuItem>
							))}
						</Select>
					);
				}
				return formatDateParkerStatusForDisplay(formatDateForDatePicker(params.row.suspensionStartDate)) || '';
			}
			return null;
		},
		resizable: false,
		sortable: false,
	},
	{
		field: 'suspensionEndDate',
		headerName: 'Suspension End Date',
		headerAlign: 'center' as const,
		renderCell: (params) => {
			if (params.row.action !== 'Terminate') {
				if (editingRowIds.includes(params.id)) {
					return (
						<Select
							value={
								updateActionMode
									? formatDateParkerStatusForDisplay(
											formatDateForDatePicker(params.row.suspensionEndDate),
										)
									: params.row.suspensionEndDate || ''
							}
							onChange={(e) => {
								setHasActionChange(true);
								params.row.suspensionEndDate = e.target.value;
								handleActionInfoUpdate(params.row.id, 'suspensionEndDate', e.target.value);
							}}
							style={{ width: '100%' }}
							disabled={params.row.noEndDate}
						>
							{lastDays?.map((option, index) => (
								<MenuItem key={index} value={formatDateParkerStatusForDisplay(option)}>
									{formatDateParkerStatusForDisplay(option)}
								</MenuItem>
							))}
						</Select>
					);
				}
				return formatDateParkerStatusForDisplay(formatDateForDatePicker(params.row.suspensionEndDate)) || '';
			}
			return null;
		},
		cellClassName: 'tableCell',
		flex: 1.5,
		resizable: false,
		sortable: false,
	},
	{
		field: 'noEndDate',
		headerName: 'No End Date',
		headerAlign: 'center' as const,
		renderCell: (params) =>
			editingRowIds.includes(params.id) && params.row.action !== 'Terminate' ? (
				<Checkbox
					color='primary'
					checked={params.row.noEndDate || false}
					onChange={(e) => {
						setHasActionChange(true);
						params.row.noEndDate = e.target.checked;
						handleActionInfoUpdate(params.row.id, 'noEndDate', e.target.checked);
						if (params.row.noEndDate === true) {
							handleActionInfoUpdate(params.row.id, 'suspensionEndDate', null);
						}
					}}
				/>
			) : (
				params.row.noEndDate || false
			),
		cellClassName: 'tableCell',
		flex: 1.5,
		resizable: false,
		sortable: false,
	},
	{
		field: 'terminationDate',
		headerName: 'Termination Date',
		headerAlign: 'center' as const,
		renderCell: (params) => {
			if (editingRowIds.includes(params.id) && params.row.action === 'Terminate') {
				return (
					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DatePicker
							value={
								updateActionMode
									? convertToDate(formatDateForDatePicker(params.row.terminationDate))
									: params.row.terminationDate
							}
							minDate={yesterday}
							onChange={(newValue) => {
								setHasActionChange(true);
								params.row.terminationDate = newValue;
								handleActionInfoUpdate(params.row.id, 'terminationDate', newValue);
							}}
							sx={{ width: '100%' }}
						/>
					</LocalizationProvider>
				);
			}
			if (params.row.action === 'Suspend') {
				return null;
			}
			return formatDateParkerStatusForDisplay(params.row.terminationDate);
		},
		cellClassName: 'tableCell',
		flex: 1.5,
		resizable: false,
		sortable: false,
	},
	{
		field: 'notes',
		headerName: 'Notes',
		headerAlign: 'center' as const,
		renderCell: (params) =>
			editingRowIds.includes(params.id) ? (
				<TextField
					value={params.row.notes || ''}
					placeholder='Notes'
					onKeyDown={handleKeyDown}
					onChange={(e) => {
						setHasActionChange(true);
						params.row.notes = e.target.value;
						handleActionInfoUpdate(params.row.id, 'notes', e.target.value);
					}}
				/>
			) : (
				<Tooltip title={params.row.notes || ''} placement='top' arrow>
					<div>{params.row.notes || ''}</div>
				</Tooltip>
			),
		cellClassName: 'tableCell',
		flex: 1.5,
		resizable: false,
		sortable: false,
	},
	{
		field: 'actions',
		type: 'actions' as const,
		getActions: (params) => {
			if (isOwnerPortal || isWaitlistParker) {
				return [];
			}
			const rowId = params.row.id;
			const editAction = renderEditAction(rowId);
			const deleteAction = renderDeleteAction(rowId);
			const { action } = params.row;

			if (action !== 'Start Date') {
				const actions = [];

				if (editAction !== null && editAction !== undefined) {
					actions.push(editAction);
				}
				if (deleteAction !== null && deleteAction !== undefined) {
					actions.push(deleteAction);
				}
				return actions;
			}
			return [];
		},
		cellClassName: 'tableCell',
		flex: 1,
		resizable: false,
		sortable: false,
	},
];
